import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import './SavedQuotations.css';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const formatPrice = (price) => {
  if (price == null) return '—';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(price);
};

const SavedQuotations = () => {
  const navigate = useNavigate();
  const { selectedSubscriberId } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    if (!selectedSubscriberId) {
      setQuotations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/api/quotations', {
        headers: { 'X-Subscriber-Id': selectedSubscriberId }
      });
      setQuotations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Load quotations error:', err);
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [selectedSubscriberId]);

  const handleOpen = (quotationId) => {
    navigate('/', { state: { loadQuotationId: quotationId } });
  };

  const handleDelete = async (quotationId, clientName) => {
    if (!window.confirm(`Weet je zeker dat je de offerte voor "${clientName || 'Onbekend'}" wilt verwijderen? Dit kan niet ongedaan worden.`)) return;
    setDeletingId(quotationId);
    try {
      await api.delete(`/api/quotations/${quotationId}`);
      setQuotations(prev => prev.filter(q => q.id !== quotationId));
    } catch (err) {
      alert('Verwijderen mislukt: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  // Client-side search filter
  const query = searchQuery.trim().toLowerCase();
  const filtered = query
    ? quotations.filter(q =>
        (q.client_name || '').toLowerCase().includes(query) ||
        (q.quotation_number || '').toLowerCase().includes(query) ||
        (q.title || '').toLowerCase().includes(query) ||
        formatDate(q.created_at).includes(query)
      )
    : quotations;

  return (
    <div className="saved-quotations-page">
      <div className="saved-quotations-header">
        <h2>Opgeslagen offertes</h2>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate('/')}
        >
          + Nieuwe offerte
        </button>
      </div>

      {!loading && quotations.length > 0 && (
        <div className="saved-quotations-search">
          <input
            type="text"
            placeholder="Zoek op klant, offertenummer, titel of datum..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <span className="search-count">{filtered.length} van {quotations.length} offertes</span>
          )}
        </div>
      )}

      {loading ? (
        <p className="saved-quotations-loading">Laden...</p>
      ) : quotations.length === 0 ? (
        <div className="saved-quotations-empty">
          <p>Nog geen opgeslagen offertes voor dit bedrijf.</p>
          <p style={{ fontSize: 13, color: '#6b7280' }}>
            Maak een offerte aan via de wizard. Na stap 5 (Voorbeeld) wordt de offerte hier opgeslagen.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="saved-quotations-empty">
          <p>Geen offertes gevonden voor "{searchQuery}".</p>
        </div>
      ) : (
        <div className="saved-quotations-table-wrap">
          <table className="saved-quotations-table">
            <thead>
              <tr>
                <th>Offerte</th>
                <th>Klant</th>
                <th>Bedrag</th>
                <th>Datum</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => (
                <tr key={q.id}>
                  <td>
                    <span className="quotation-name">
                      {q.client_name || 'Onbekend'} — {formatDate(q.created_at)}
                    </span>
                    {q.quotation_number && (
                      <span className="quotation-number">{q.quotation_number}</span>
                    )}
                  </td>
                  <td>{q.client_name || '—'}</td>
                  <td className="quotation-price">{formatPrice(q.total_price)}</td>
                  <td className="quotation-date">{formatDate(q.created_at)}</td>
                  <td className="quotation-actions">
                    <button
                      type="button"
                      className="btn btn-primary btn-small"
                      onClick={() => handleOpen(q.id)}
                    >
                      Openen
                    </button>
                    <button
                      type="button"
                      className="btn btn-delete btn-small"
                      disabled={deletingId === q.id}
                      onClick={() => handleDelete(q.id, q.client_name)}
                    >
                      {deletingId === q.id ? '...' : 'Verwijderen'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="saved-quotations-note">
        Offertes worden automatisch na 3 maanden verwijderd.
      </p>
    </div>
  );
};

export default SavedQuotations;
